import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, DollarSign, TrendingUp, AlertCircle, Percent, CalendarClock } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import UpcomingDueCustomers from './UpcomingDueCustomers';
import InstallmentSummaryCards from './InstallmentSummaryCards';
import GreetingHeader from './GreetingHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RecoveryOfficerDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/dashboard/recovery-officer-stats`, {
        withCredentials: true,
      });
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-400">Loading dashboard...</div>
      </div>
    );
  }

  const canSeeRemaining = !!stats?.can_view_remaining_amount;
  // Iter34 — RO's own performance numbers: Monthly Recoverable (target),
  // Monthly Recovered (collected this month · resets on 1st), Today's
  // Received (collected today · resets at midnight). These reflect the
  // RO's collection performance and are shown even though outstanding
  // customer amounts stay hidden per iter33 policy.
  const statCards = [
    { title: 'Assigned Customers', value: stats?.total_assigned_customers || 0, icon: Users, color: 'text-blue-500', testId: 'recovery-stat-assigned' },
    { title: "Today's Due", value: `${stats?.today_due_count || 0}`, icon: CalendarClock, color: 'text-amber-500', testId: 'recovery-stat-today' },
    { title: 'Upcoming (7 days)', value: `${stats?.upcoming_count || 0}`, icon: CalendarClock, color: 'text-cyan-400', testId: 'recovery-stat-upcoming' },
    { title: 'Overdue', value: `${stats?.overdue_count || 0}`, icon: AlertCircle, color: 'text-red-500', testId: 'recovery-stat-overdue' },
    { title: 'Paid Installments', value: `${stats?.paid_installments_count || 0}`, icon: TrendingUp, color: 'text-emerald-500', testId: 'recovery-stat-paid' },
    { title: "Today's Received", value: `PKR ${(stats?.today_received || 0).toLocaleString()}`, icon: DollarSign, color: 'text-emerald-400', testId: 'recovery-stat-today-received' },
    { title: 'Monthly Recovered', value: `PKR ${(stats?.monthly_recovered || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-emerald-500', testId: 'recovery-stat-monthly-recovered' },
    { title: 'Monthly Recoverable', value: `PKR ${(stats?.monthly_recoverable || 0).toLocaleString()}`, icon: DollarSign, color: 'text-amber-500', testId: 'recovery-stat-recoverable' },
    { title: 'Total Recovered', value: `PKR ${(stats?.total_recovered || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-emerald-500', testId: 'recovery-stat-recovered' },
    { title: 'Recovery Performance', value: `${(stats?.recovery_performance || 0).toFixed(1)}%`, icon: Percent, color: 'text-emerald-500', testId: 'recovery-stat-performance' },
  ];

  return (
    <div className="space-y-6" data-testid="recovery-officer-dashboard">
      <GreetingHeader
        testId="recovery-greeting"
        fallbackName="Recovery Officer"
        subtitle="Recovery Dashboard · Track your assigned customers and recovery performance"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="bg-slate-900 border-slate-800 hover:-translate-y-1 transition-transform" data-testid={stat.testId}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">
                {stat.title}
              </CardTitle>
              <stat.icon className={`h-4 w-4 ${stat.color}`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {canSeeRemaining ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-slate-900 border-slate-800" data-testid="ro-chart-buckets">
            <CardHeader><CardTitle className="text-white text-base">Installment Buckets</CardTitle></CardHeader>
            <CardContent style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Today Due', count: stats?.today_due_count || 0 },
                  { name: 'Upcoming', count: stats?.upcoming_count || 0 },
                  { name: 'Overdue', count: stats?.overdue_count || 0 },
                  { name: 'Paid', count: stats?.paid_installments_count || 0 },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', color: '#fff' }} />
                  <Bar dataKey="count" fill="#F59E0B" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800" data-testid="ro-chart-recovery">
            <CardHeader><CardTitle className="text-white text-base">Recovered vs Remaining</CardTitle></CardHeader>
            <CardContent style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={[
                    { name: 'Recovered', value: Math.max(0, stats?.total_recovered || 0) },
                    { name: 'Remaining', value: Math.max(0, stats?.remaining_amount || 0) },
                  ]} dataKey="value" nameKey="name" innerRadius={45} outerRadius={85} paddingAngle={2}>
                    <Cell fill="#10B981" /><Cell fill="#F59E0B" />
                  </Pie>
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', color: '#fff' }} />
                  <Legend wrapperStyle={{ color: '#cbd5e1', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card className="bg-slate-900 border-slate-800" data-testid="ro-chart-buckets">
          <CardHeader><CardTitle className="text-white text-base">Installment Buckets</CardTitle></CardHeader>
          <CardContent style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { name: 'Today Due', count: stats?.today_due_count || 0 },
                { name: 'Upcoming', count: stats?.upcoming_count || 0 },
                { name: 'Overdue', count: stats?.overdue_count || 0 },
                { name: 'Paid', count: stats?.paid_installments_count || 0 },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', color: '#fff' }} />
                <Bar dataKey="count" fill="#F59E0B" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card className="bg-slate-900 border-slate-800" data-testid="ro-chart-trend">
        <CardHeader><CardTitle className="text-white text-base">My 30-Day Collection Trend</CardTitle></CardHeader>
        <CardContent style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={(stats?.trend_30d) || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} />
              <YAxis stroke="#94a3b8" fontSize={11} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', color: '#fff' }}
                formatter={(v) => `PKR ${Number(v).toLocaleString()}`} />
              <Line type="monotone" dataKey="amount" stroke="#F59E0B" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <InstallmentSummaryCards />
      <UpcomingDueCustomers title="My Upcoming, Due & Overdue Installments" />

      {stats?.customers && stats.customers.length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Assigned Customers</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400">Customer Name</TableHead>
                  <TableHead className="text-slate-400">Account Number</TableHead>
                  <TableHead className="text-slate-400">Mobile</TableHead>
                  <TableHead className="text-slate-400">Next Due</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.customers.slice(0, 20).map((customer, index) => (
                  <TableRow key={index} className="border-slate-800 hover:bg-slate-800/50" data-testid="assigned-customer-row">
                    <TableCell className="text-white">{customer.customer_name}</TableCell>
                    <TableCell className="text-slate-300">{customer.account_number}</TableCell>
                    <TableCell className="text-slate-300">{customer.mobile}</TableCell>
                    <TableCell className="text-slate-300 text-xs">
                      {customer.next_due_date ? new Date(customer.next_due_date).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'numeric'}) : '—'}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-sm text-xs ${
                        customer.status === 'active' ? 'bg-emerald-500/20 text-emerald-500' :
                        customer.status === 'defaulted' ? 'bg-red-500/20 text-red-500' :
                        'bg-yellow-500/20 text-yellow-500'
                      }`}>
                        {customer.status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}